import { Link } from 'react-router-dom'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { type TableColumn } from 'react-data-table-component'
import FormattedDate from '../../components/FormattedDate'
import { Roles } from '../../components/Roles/Roles'
import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { isAdmin, isOwner } from '../../common/userUtils'
import { type User } from '../../models/User'
import { UserAvatarInline } from '../../components/UserAvatar/UserAvatarInline'
import { TeamStatusChip } from './TeamStatusChip'
import { type TeamRowActionHandlers, TeamRowActions } from './TeamRowActions'
import { type TeamRow } from './teamRows'
import { dateSortValue } from './utils'

const canOpenMemberPage = (row: TeamRow, authenticatedUser?: User | null) =>
  row.kind === 'member' && (row.id === authenticatedUser?.id || isAdmin(authenticatedUser) || isOwner(authenticatedUser))

const FAILED_TO_DELIVER_HINT =
  "Invitation not delivered. We'll keep trying until it expires. To fix it now, resend or revoke this invitation and send a new one."

export interface TeamColumnOptions {
  showExpiresAt?: boolean
}

export const buildTeamColumns = (authenticatedUser?: User | null, { showExpiresAt = false }: TeamColumnOptions = {}): TableColumn<TeamRow>[] => [
  {
    name: '',
    sortable: false,
    compact: true,
    width: '24px',
    cell: (row) => <UserAvatarInline user={row.kind === 'member' ? row.member : undefined} name={row.name} />,
  },
  {
    name: 'Name',
    selector: (row) => row.name,
    sortable: true,
    grow: 2,
    cell: (row) => (
      <div className='d-flex align-items-center gap-2'>
        {row.status === 'failedToDeliver' ? (
          <OverlayTrigger placement='top' overlay={<Tooltip>{FAILED_TO_DELIVER_HINT}</Tooltip>}>
            <i className='bi bi-exclamation-triangle-fill zocks-text-warning-dark' aria-label='Invitation not delivered' />
          </OverlayTrigger>
        ) : null}
        {canOpenMemberPage(row, authenticatedUser) ? (
          <Link className='zocks-text-primary' to={`/account/manage-team/users/${row.id}`}>
            {row.name}
          </Link>
        ) : (
          row.name
        )}
      </div>
    ),
  },
  {
    name: 'Email',
    selector: (row) => row.email,
    sortable: true,
    grow: 2,
    width: '25%',
  },
  {
    name: 'Role',
    selector: (row) => JSON.stringify(row.roleIds),
    sortable: true,
    style: { wordBreak: 'normal' },
    cell: (row) => <Roles userRoleIds={row.roleIds} />,
  },
  {
    name: 'Date added',
    selector: (row) => dateSortValue(row.createdAt),
    sortable: true,
    cell: (row) => <FormattedDate date={row.createdAt ? formatDate(row.createdAt) : ''} />,
    hide: 992,
    grow: 2,
  },
  ...(showExpiresAt
    ? [
        {
          name: 'Expires at',
          selector: (row) => dateSortValue(row.expiresAt),
          sortable: true,
          cell: (row) => <FormattedDate date={row.expiresAt ? formatDate(row.expiresAt) : ''} />,
          hide: 992,
          grow: 2,
        } satisfies TableColumn<TeamRow>,
      ]
    : []),
  {
    name: 'Status',
    selector: (row) => row.status,
    sortable: true,
    cell: (row) => (row.status ? <TeamStatusChip status={row.status} /> : null),
  },
]

export const buildTeamActionsColumn = (handlers: TeamRowActionHandlers): TableColumn<TeamRow> => ({
  ignoreRowClick: true,
  right: true,
  button: true,
  allowOverflow: true,
  width: '3rem',
  cell: (row) => <TeamRowActions row={row} {...handlers} />,
})
